package com.snakesworld.game.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.snakesworld.game.APP_VERSION
import com.snakesworld.game.ui.theme.GameCanvasColors
import com.snakesworld.game.ui.theme.LocalGameCanvasColors
import com.snakesworld.game.ui.theme.appFontFamily
import com.snakesworld.game.viewmodel.MenuViewModel
import org.jetbrains.compose.resources.stringResource
import snakesworld.shared.generated.resources.Res
import snakesworld.shared.generated.resources.hub_subtitle
import snakesworld.shared.generated.resources.hub_version_label
import snakesworld.shared.generated.resources.multiplayer
import snakesworld.shared.generated.resources.multiplayer_desc
import snakesworld.shared.generated.resources.settings
import snakesworld.shared.generated.resources.settings_desc
import snakesworld.shared.generated.resources.single_player
import snakesworld.shared.generated.resources.single_player_desc

@Composable
fun HubScreen(
    viewModel: MenuViewModel,
    onSinglePlayer: () -> Unit,
    onMultiplayer: () -> Unit,
    onSettings: () -> Unit,
) {
    val canvasColors = LocalGameCanvasColors.current

    LaunchedEffect(Unit) {
        viewModel.loadProfile()
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(canvasColors.backgroundColor)
            .windowInsetsPadding(WindowInsets.safeDrawing)
    ) {
        BoxWithConstraints(modifier = Modifier.fillMaxSize().padding(16.dp)) {
            val isWide = maxWidth > 720.dp
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState()),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Spacer(modifier = Modifier.height(8.dp))
                AppBrandTitle(canvasColors = canvasColors)
                Text(
                    text = stringResource(Res.string.hub_subtitle),
                    color = canvasColors.accentColor,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Bold,
                    fontFamily = appFontFamily(),
                    letterSpacing = 1.sp
                )
                Spacer(modifier = Modifier.height(8.dp))

                if (isWide) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        HubModeButton(
                            title = stringResource(Res.string.single_player),
                            description = stringResource(Res.string.single_player_desc),
                            onClick = onSinglePlayer,
                            accent = canvasColors.hudTextColor,
                            canvasColors = canvasColors,
                            modifier = Modifier.weight(1f)
                        )
                        HubModeButton(
                            title = stringResource(Res.string.multiplayer),
                            description = stringResource(Res.string.multiplayer_desc),
                            onClick = onMultiplayer,
                            accent = canvasColors.boundaryColor,
                            canvasColors = canvasColors,
                            modifier = Modifier.weight(1f)
                        )
                        HubModeButton(
                            title = stringResource(Res.string.settings),
                            description = stringResource(Res.string.settings_desc),
                            onClick = onSettings,
                            accent = canvasColors.accentColor,
                            canvasColors = canvasColors,
                            modifier = Modifier.weight(1f)
                        )
                    }
                } else {
                    HubModeButton(
                        title = stringResource(Res.string.single_player),
                        description = stringResource(Res.string.single_player_desc),
                        onClick = onSinglePlayer,
                        accent = canvasColors.hudTextColor,
                        canvasColors = canvasColors,
                    )
                    HubModeButton(
                        title = stringResource(Res.string.multiplayer),
                        description = stringResource(Res.string.multiplayer_desc),
                        onClick = onMultiplayer,
                        accent = canvasColors.boundaryColor,
                        canvasColors = canvasColors,
                    )
                    HubModeButton(
                        title = stringResource(Res.string.settings),
                        description = stringResource(Res.string.settings_desc),
                        onClick = onSettings,
                        accent = canvasColors.accentColor,
                        canvasColors = canvasColors,
                    )
                }

                Spacer(modifier = Modifier.height(8.dp))
                Text(
                    text = stringResource(Res.string.hub_version_label, APP_VERSION),
                    color = canvasColors.hudTextColor.copy(alpha = 0.8f),
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Bold,
                    fontFamily = appFontFamily(),
                    letterSpacing = 1.sp,
                    textAlign = TextAlign.Center
                )
                Spacer(modifier = Modifier.height(16.dp))
            }
        }
    }
}

@Composable
private fun HubModeButton(
    title: String,
    description: String,
    onClick: () -> Unit,
    accent: Color,
    canvasColors: GameCanvasColors,
    modifier: Modifier = Modifier,
) {
    Button(
        onClick = onClick,
        colors = ButtonDefaults.buttonColors(
            containerColor = canvasColors.hudBgColor,
            contentColor = accent,
        ),
        shape = RoundedCornerShape(10.dp),
        modifier = modifier
            .fillMaxWidth()
            .height(120.dp)
            .border(2.dp, accent, RoundedCornerShape(10.dp))
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            Text(
                text = title,
                color = accent,
                fontSize = 18.sp,
                fontWeight = FontWeight.Black,
                fontFamily = appFontFamily(),
                letterSpacing = 1.5.sp,
                textAlign = TextAlign.Center
            )
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = description,
                color = canvasColors.hudTextColor.copy(alpha = 0.85f),
                fontSize = 11.sp,
                fontWeight = FontWeight.Bold,
                fontFamily = appFontFamily(),
                textAlign = TextAlign.Center
            )
        }
    }
}
